import React, { useState, useCallback, useEffect } from 'react';
import './ContactForm.css';
import mailIcon from '../asset/icons/mail-icon.svg';
import imageIcon from '../asset/icons/image-icon.svg';
import Modal from './Modal';

const ContactForm = () => {
  // State per i dati del form
  const [formData, setFormData] = useState({
    name: '',
    message: ''
  });

  // State per metodo di contatto
  const [contactMethod, setContactMethod] = useState('email');
  const [contactInput, setContactInput] = useState('');
  const [contactInputError, setContactInputError] = useState('');

  // State per UI e validazione
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  // State per toast messages
  const [toastState, setToastState] = useState({
    visible: false,
    type: null, // 'success' or 'error'
    message: ''
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [privacyContent, setPrivacyContent] = useState('');

  // Gestori per i campi del form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleContactMethodChange = (method) => {
    setContactMethod(method);
    setContactInput('');
  };

  const handleContactInputChange = (e) => {
    setContactInput(e.target.value);
    setContactInputError('');
  };

  const handleConsentChange = (e) => {
    setIsConsentChecked(e.target.checked);
  };

  // Funzione di validazione specifica per contactInput
  const validateContactInput = useCallback((method, input) => {
    let isValid = true;
    let errorMessage = '';

    if (method === 'email') {
      const emailRegex = /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,7}$/;
      if (!emailRegex.test(input)) {
        isValid = false;
        errorMessage = 'Per favore, inserisci un indirizzo email valido.';
      }
    } else if (method === 'phone' || method === 'whatsapp') {
      const phoneRegex = /^[\d\s()+-]+$/;
      if (!phoneRegex.test(input)) {
        isValid = false;
        errorMessage = 'Per favore, inserisci un numero di telefono valido.';
      }
    }

    return { isValid, errorMessage };
  }, []);

  // Function to hide toast message
  const hideToast = useCallback(() => {
    setToastState({ visible: false, type: null, message: '' });
  }, []);

  // Auto-hide toast after 12 seconds
  useEffect(() => {
    if (toastState.visible) {
      const timer = setTimeout(() => {
        hideToast();
      }, 12000);
      return () => clearTimeout(timer);
    }
  }, [toastState.visible, hideToast]);

  // Funzione per ottenere il placeholder in base al metodo di contatto (memoizzata)
  const getPlaceholderText = useCallback(() => {
    switch (contactMethod) {
      case 'email':
        return 'mario.rossi@email.com';
      case 'phone':
      case 'whatsapp':
        return '+39 123456789';
      default:
        return '';
    }
  }, [contactMethod]);

  const handlePrivacyClick = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/privacy-policy.html');
      const html = await res.text();
      const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      setPrivacyContent(match ? match[1] : html);
      setModalOpen(true);
    } catch {
      setPrivacyContent('<p>Errore nel caricamento della Privacy Policy.</p>');
      setModalOpen(true);
    }
  };

  // Custom form submit handler to override Netlify default behavior
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate contact input again before submit
    const { isValid, errorMessage } = validateContactInput(contactMethod, contactInput);
    if (!isValid) {
      setContactInputError(errorMessage);
      setToastState({ visible: true, type: 'error', message: errorMessage });
      return;
    }
    // Prepare form data
    const form = e.target;
    const data = new FormData(form);
    data.set('contactValue', contactInput);
    data.set('contactMethod', contactMethod);
    data.set('name', formData.name);
    data.set('message', formData.message);
    data.set('consent', isConsentChecked ? 'on' : '');
    data.set('submissionDate', new Date().toISOString());
    // Netlify requires the form-name field
    data.set('form-name', 'contact-form');
    try {
      await fetch('/', {
        method: 'POST',
        body: data,
      });
      setToastState({ visible: true, type: 'success', message: 'Messaggio inviato con successo! Ti risponderò al più presto.' });
      // Reset form fields
      setFormData({ name: '', message: '' });
      setContactInput('');
      setIsConsentChecked(false);
      setContactInputError('');
      // Optionally, reset file input if/when re-enabled
    } catch (error) {
      setToastState({ visible: true, type: 'error', message: 'Si è verificato un errore durante l\'invio. Riprova più tardi.' });
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-background custom-bg">
        <svg className="wave-divider-top" viewBox="0 0 1280 106" preserveAspectRatio="none">
          <path d="M-0.5 20.8099C479.611 130.312 761.784 130.814 1280.5 20.8099V0.125H-0.5V20.8099Z" fill="var(--background)"></path>
        </svg>
        
        <div className="container">
          {/* imageIcon is imported for future use. The following hidden span ensures it is used to avoid lint errors. Do not remove. */}
          <span style={{ display: 'none' }} aria-hidden="true">{imageIcon}</span>
          <div className="contact-form-container">
            <h2 className="contact-title">Contattami per un preventivo gratuito</h2>
            
            <div className="contact-callout">
              <p>Sei un professionista e vorresti collaborare con me?<br />Scrivimi una mail a <a className="footer-contact-txt links" href="mailto:sarabocca.restauri@gmail.com">sarabocca.restauri@gmail.com</a></p>
            </div>
            
            {/* Form principale visibile */}
            <form 
              className="contact-form" 
              name="contact-form"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              encType="multipart/form-data"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="form-name" value="contact-form" />
              
              {/* Honeypot field per protezione spam */}
              <div style={{ display: 'none' }}>
                <label>
                  Don't fill this out if you're human: <input name="bot-field" />
                </label>
              </div>
              
              {/* Campo Nome */}
              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor="name" className="form-label">Il tuo nome*</label>
                  <span className="required-text">obbligatorio</span>
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  placeholder="Mario Rossi"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              {/* Campo Metodo di Contatto */}
              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor="contactInput" className="form-label">Come preferisci essere ricontattato?*</label>
                  <span className="required-text">obbligatorio</span>
                </div>
                <div className="contact-method-selector">
                  <button
                    type="button"
                    className={`method-tab ${contactMethod === 'email' ? 'active' : ''}`}
                    onClick={() => handleContactMethodChange('email')}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={`method-tab ${contactMethod === 'phone' ? 'active' : ''}`}
                    onClick={() => handleContactMethodChange('phone')}
                  >
                    Chiamata
                  </button>
                  <button
                    type="button"
                    className={`method-tab ${contactMethod === 'whatsapp' ? 'active' : ''}`}
                    onClick={() => handleContactMethodChange('whatsapp')}
                  >
                    WhatsApp
                  </button>
                </div>
                <input
                  type={contactMethod === 'email' ? 'email' : 'text'}
                  id="contactInput"
                  name="contactValue"
                  className="form-input"
                  placeholder={getPlaceholderText()}
                  value={contactInput}
                  onChange={handleContactInputChange}
                  onBlur={() => {
                    const { errorMessage } = validateContactInput(contactMethod, contactInput);
                    setContactInputError(errorMessage);
                  }}
                  required
                />
                {/* Campo nascosto per il metodo di contatto selezionato */}
                <input type="hidden" name="contactMethod" value={contactMethod} />
                {/* Display validation error message */}
                {contactInputError && (
                  <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '5px' }}>
                    {contactInputError}
                  </p>
                )}
              </div>
              
              {/* Campo Messaggio */}
              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor="message" className="form-label">Raccontami della tua opera<br />e di cosa hai bisogno*</label>
                  <span className="required-text">obbligatorio</span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  className="form-textarea"
                  placeholder="Descrivi brevemente..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>
              
              {/* Campo File Upload */}
              {/*
              // --- IN FUTURO NON CANCELLARE QUESTO BLOCCO: FEATURE UPLOAD FOTO ---
              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor="files" className="form-label">Fotografie dell'opera</label>
                  <span className="file-description">Almeno una foto mi aiuta a capire meglio le tue necessità</span>
                </div>
                <div className="file-upload-container">
                  <div className="file-upload-icon">
                    <img src={imageIcon} alt="Immagine" width="32" height="32" />
                  </div>
                  <div className="file-upload-text">
                    <p>Trascina qui le immagini oppure <label htmlFor="files" className="links" style={{cursor: 'pointer'}}>sfoglia</label></p>
                    <p className="file-types">Supporta file .jpg, .jpeg, .png, .gif, .heif fino a 10MB</p>
                  </div>
                  <input
                    type="file"
                    id="files"
                    name="files"
                    className="file-input"
                    accept=".jpg,.jpeg,.png,.gif,.heif"
                    multiple
                  />
                </div>
              </div>
              // --- FINE BLOCCO UPLOAD FOTO ---
              */}
              
              {/* Campo Consenso Privacy */}
              <div className="form-group">
                <div className="form-check-container">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    checked={isConsentChecked}
                    onChange={handleConsentChange}
                    required
                    aria-required="true"
                  />
                  <label htmlFor="consent" className="form-label form-check-label">
                    Acconsento al <a href="/privacy-policy.html" className="links" onClick={handlePrivacyClick} target="_blank" rel="noopener noreferrer">trattamento dei miei dati</a> per ricevere una risposta alla mia richiesta. <span className="required-text">*</span>
                  </label>
                </div>
              </div>

              {/* Campo nascosto per data di invio */}
              <input type="hidden" name="submissionDate" value={new Date().toISOString()} />

              {/* Bottone Submit */}
              <div className="submit-btn">
                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={!formData.name || !contactInput || !formData.message || !isConsentChecked}
                >
                  <img src={mailIcon} className="btn-mail-icon" alt="Mail" />
                  Richiedi un preventivo gratuito
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <svg className="wave-divider-bottom" viewBox="0 0 1440 100" preserveAspectRatio="none">
          <path d="M0,0 C240,95 480,100 720,80 C960,60 1200,20 1440,0 L1440,100 L0,100 Z" fill="var(--background)"></path>
        </svg>
      </div>

      {/* Toast Message */}
      <div aria-live="polite">
        {toastState.visible && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '15px 20px',
            borderRadius: '8px',
            color: toastState.type === 'success' ? '#155724' : '#721c24',
            backgroundColor: toastState.type === 'success' ? '#d4edda' : '#f8d7da',
            border: `1px solid ${toastState.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            zIndex: 2147483647, // Highest z-index
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          }}>
            <span style={{ marginRight: '15px' }}>{toastState.message}</span>
            <button
              onClick={hideToast}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: toastState.type === 'success' ? '#155724' : '#721c24',
                padding: '0',
                lineHeight: '1',
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div dangerouslySetInnerHTML={{ __html: privacyContent }} />
      </Modal>
    </section>
  );
};

export default ContactForm;